import { useEffect, useState } from 'react';
import {
  ExerciseSpecificDTO,
  ExerciseTypeDTO,
  ExerciseSpecificTargetDTO,
  MemberFirearmDTO,
} from '../../services/apiClient';
import FormField from '../common/FormField';
import Button from '../common/Button';
import { Download, Link as LinkIcon } from 'lucide-react';
import Loader from '../common/Loader';
import { useMemberFirearmApi } from '../../hooks/useMemberFirearmApi';
import { useFirearmSpecificWithExercisesById } from '../../hooks/useFirearmSpecificApi';
import { useExerciseTargetApi } from '../../hooks/useExerciseTargetApi';
import { filterExerciseTypesByCompetition } from '../../utils/filterExerciseTypes';
import { showAlert } from '../../utils/sweetAlertUtils';
import { downloadFileFromUrl } from '../../utils/downloadUtils';
import { buildFirearmOptions } from '../../utils/formUtils/buildFirearmOptions';

interface DownloadCompetitionTargetsProps {
  memberId: string;
  competitionEntryId: string;
  onClose: () => void;
}

const DownloadCompetitionTargets: React.FC<DownloadCompetitionTargetsProps> = ({
  memberId,
  competitionEntryId,
  onClose,
}) => {
  const [memberFirearms, setMemberFirearms] = useState<MemberFirearmDTO[]>([]);
  const [isLoadingMemberFirearms, setIsLoadingMemberFirearms] = useState(false);
  const [memberFirearmId, setMemberFirearmId] = useState<string>('');
  const [exerciseTypeId, setExerciseTypeId] = useState<string>('');
  const [exerciseSpecificId, setExerciseSpecificId] = useState<string>('');
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseTypeDTO[]>([]);
  const [exerciseSpecifics, setExerciseSpecifics] = useState<ExerciseSpecificDTO[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<ExerciseSpecificDTO | null>(null);
  const [exerciseTargets, setExerciseTargets] = useState<ExerciseSpecificTargetDTO[]>([]);
  const [downloadingTargetId, setDownloadingTargetId] = useState<string | null>(null);

  const { search: searchMemberFirearms } = useMemberFirearmApi();
  const { downloadPostalTarget } = useExerciseTargetApi();

  const selectedMemberFirearm = memberFirearms.find((f) => f.id === memberFirearmId);
  const { data: firearmSpecific, isLoading: isLoadingFirearmSpecific } =
    useFirearmSpecificWithExercisesById(selectedMemberFirearm?.firearmSpecificId);

  useEffect(() => {
    const loadMemberFirearms = async () => {
      if (!memberId) return;
      setIsLoadingMemberFirearms(true);
      try {
        const res = await searchMemberFirearms({
          filters: { memberId },
          page: 1,
          pageSize: 9999,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setMemberFirearms(res?.data?.results ?? []);
      } catch (err) {
        console.error('Error loading member firearms:', err);
      } finally {
        setIsLoadingMemberFirearms(false);
      }
    };

    loadMemberFirearms();
  }, [memberId]);

  // Filter exercise types/specifics to competition-eligible ones once the firearm's exercises load
  useEffect(() => {
    if (!firearmSpecific || !selectedMemberFirearm) {
      setExerciseTypes([]);
      setExerciseSpecifics([]);
      return;
    }

    const { exerciseTypes: allowedTypes } = filterExerciseTypesByCompetition(
      firearmSpecific,
      undefined,
      selectedMemberFirearm,
    );
    setExerciseTypes(allowedTypes);
  }, [firearmSpecific, selectedMemberFirearm]);

  // When exercise type changes, filter the exercise specifics for that type
  useEffect(() => {
    if (exerciseTypeId && firearmSpecific?.exercises) {
      const selectedExercise = firearmSpecific.exercises.find(
        (ex: any) => ex.exerciseTypeId === exerciseTypeId,
      );
      const specifics = (selectedExercise?.exerciseSpecifics || []).filter(
        (specific: ExerciseSpecificDTO) => specific.isCompetition === true,
      );
      setExerciseSpecifics(specifics);
      setExerciseSpecificId('');
      setSelectedTarget(null);
      setExerciseTargets([]);
    } else {
      setExerciseSpecifics([]);
      setExerciseSpecificId('');
      setSelectedTarget(null);
      setExerciseTargets([]);
    }
  }, [exerciseTypeId, firearmSpecific]);

  // When exercise specific changes, set the selected target and extract targets
  useEffect(() => {
    if (exerciseSpecificId) {
      const target = exerciseSpecifics.find((ex) => ex.id === exerciseSpecificId);
      setSelectedTarget(target || null);
      setExerciseTargets(target?.exerciseSpecificTargets || []);
    } else {
      setSelectedTarget(null);
      setExerciseTargets([]);
    }
  }, [exerciseSpecificId, exerciseSpecifics]);

  const handleFirearmChange = (id: string) => {
    setMemberFirearmId(id);
    setExerciseTypeId('');
    setExerciseSpecificId('');
    setSelectedTarget(null);
    setExerciseTargets([]);
  };

  const handleDownloadTarget = async (target: ExerciseSpecificTargetDTO) => {
    if (!target.exerciseTarget?.id) {
      await showAlert({
        icon: 'error',
        title: 'Error',
        text: 'Target information is missing',
      });
      return;
    }

    setDownloadingTargetId(target.exerciseTarget.id);
    try {
      const sasUri = await downloadPostalTarget(
        target.exerciseTarget.id,
        competitionEntryId,
        exerciseTypeId || undefined,
        exerciseSpecificId || undefined,
      );
      if (sasUri) {
        await downloadFileFromUrl(
          sasUri,
          `competition-target-${target.exerciseTarget.code}.pdf`,
          () => {
            console.log('Target downloaded successfully');
          },
          async () => {
            await showAlert({
              icon: 'error',
              title: 'Download Failed',
              text: 'Failed to download competition target PDF',
            });
          },
        );
      }
    } catch (error: any) {
      await showAlert({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to generate competition target PDF',
      });
    } finally {
      setDownloadingTargetId(null);
    }
  };

  if (isLoadingMemberFirearms) {
    return <Loader />;
  }

  if (memberFirearms.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        You have no firearms registered on your profile.
      </div>
    );
  }

  return (
    <>
      <FormField
        id="memberFirearmId"
        label="Firearm"
        value={memberFirearmId}
        type="dropdown"
        onChange={(e) => handleFirearmChange(e.target.value)}
        placeholder="Select Firearm"
        options={buildFirearmOptions(memberFirearms)}
        isLoading={false}
      />

      {memberFirearmId && (
        <>
          <FormField
            id="exerciseTypeId"
            label="Exercise Type"
            value={exerciseTypeId}
            type="dropdown"
            onChange={(e) => setExerciseTypeId(e.target.value)}
            placeholder="Select Exercise Type"
            options={exerciseTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingFirearmSpecific}
          />
          <FormField
            id="exerciseSpecificId"
            label="Exercise Specific"
            value={exerciseSpecificId}
            type="dropdown"
            onChange={(e) => setExerciseSpecificId(e.target.value)}
            placeholder="Select Exercise Specific"
            options={exerciseSpecifics.map((specific) => ({
              value: specific.id,
              label: specific.title || '',
            }))}
            isLoading={false}
          />
        </>
      )}

      {selectedTarget && exerciseTargets.length > 0 && (
        <div className="mt-4">
          {exerciseTargets.map((target) => (
            <>
              <div
                key={`${target.exerciseTargetId}-rules`}
                className="flex items-center justify-between p-4 mb-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Shooting Target (Rules)</h4>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="primary"
                    onClick={() =>
                      downloadFileFromUrl(
                        target.exerciseTarget!.rulesUrl!,
                        'shooting-rules.pdf',
                      )
                    }
                  >
                    <LinkIcon size={16} />
                  </Button>
                </div>
              </div>
              <div
                key={target.exerciseTargetId}
                className="flex items-center justify-between p-4 my-5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {`Shooting Target (${target.exerciseTarget?.code})`}
                  </h4>
                </div>

                <Button
                  variant="primary"
                  onClick={() => handleDownloadTarget(target)}
                  disabled={!target.exerciseTarget?.isActive || downloadingTargetId === target.exerciseTarget?.id}
                >
                  {downloadingTargetId === target.exerciseTarget?.id ? 'Generating...' : <Download size={16} />}
                </Button>
              </div>
            </>
          ))}
        </div>
      )}

      <div className="flex justify-end space-x-4 mt-6">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
};

export default DownloadCompetitionTargets;
